import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const UserContextSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState('player'); // 'player' or 'venue_owner'
  const navigate = useNavigate();

  // Mock user data - in real app, this would come from auth context
  const userData = {
    name: 'João Silva',
    email: 'joao.silva@email.com',
    avatar: null,
    roles: [
      {
        id: 'player',
        name: 'Jogador',
        description: 'Organizar peladas e participar de eventos',
        icon: 'User',
        active: true
      },
      {
        id: 'venue_owner',
        name: 'Proprietário de Quadra',
        description: 'Gerenciar quadras e reservas',
        icon: 'Building',
        active: true
      }
    ]
  };

  const availableRoles = userData?.roles?.filter(role => role?.active);
  const hasMultipleRoles = availableRoles?.length > 1;

  const handleRoleSwitch = (roleId) => {
    setCurrentRole(roleId);
    setIsOpen(false);
    
    // Navigate to appropriate dashboard based on role
    if (roleId === 'venue_owner') {
      navigate('/venue-owner-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleProfileClick = () => {
    navigate('/user-profile');
    setIsOpen(false);
  };

  const handleLogout = () => {
    // Handle logout logic
    console.log('Logging out...');
    setIsOpen(false);
  };

  const getCurrentRoleData = () => {
    return availableRoles?.find(role => role?.id === currentRole) || availableRoles?.[0];
  };

  const currentRoleData = getCurrentRoleData();

  return (
    <div className="relative">
      {/* User Profile Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 transition-standard"
      >
        {/* Avatar */}
        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
          {userData?.avatar ? (
            <img 
              src={userData?.avatar} 
              alt={userData?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <Icon name="User" size={16} color="white" />
          )}
        </div>
        
        {/* User Info - Hidden on mobile */}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-foreground">
            {userData?.name}
          </p>
          {hasMultipleRoles && (
            <p className="text-xs text-muted-foreground font-caption">
              {currentRoleData?.name}
            </p>
          )}
        </div>
        
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={14} 
          className="text-muted-foreground hidden md:block" 
        />
      </Button>
      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          <div className="absolute right-0 mt-2 w-72 bg-popover border border-border rounded-lg shadow-elevation-3 z-50 animate-fade-in">
            {/* User Info Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  {userData?.avatar ? (
                    <img 
                      src={userData?.avatar} 
                      alt={userData?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={20} color="white" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-popover-foreground">
                    {userData?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {userData?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Role Switcher - Only show if user has multiple roles */}
            {hasMultipleRoles && (
              <div className="p-4 border-b border-border">
                <h4 className="text-sm font-medium text-popover-foreground mb-3">
                  Alternar Contexto
                </h4>
                <div className="space-y-2">
                  {availableRoles?.map((role) => (
                    <button
                      key={role?.id}
                      onClick={() => handleRoleSwitch(role?.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-standard ${
                        currentRole === role?.id
                          ? 'bg-primary/10 border border-primary/20' :'hover:bg-muted/50'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        currentRole === role?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        <Icon name={role?.icon} size={16} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`text-sm font-medium ${
                          currentRole === role?.id
                            ? 'text-primary' :'text-popover-foreground'
                        }`}>
                          {role?.name}
                        </p>
                        <p className="text-xs text-muted-foreground font-caption">
                          {role?.description}
                        </p>
                      </div>
                      {currentRole === role?.id && (
                        <Icon name="Check" size={16} className="text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Menu Items */}
            <div className="p-2">
              <button
                onClick={handleProfileClick}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-standard"
              >
                <Icon name="Settings" size={16} className="text-muted-foreground" />
                <span className="text-sm text-popover-foreground">
                  Configurações da Conta
                </span>
              </button>
              
              <button
                onClick={() => {
                  console.log('Opening help...');
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-standard"
              >
                <Icon name="HelpCircle" size={16} className="text-muted-foreground" />
                <span className="text-sm text-popover-foreground">
                  Ajuda e Suporte
                </span>
              </button>
              
              <div className="border-t border-border my-2" />
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-error/10 text-error transition-standard"
              >
                <Icon name="LogOut" size={16} />
                <span className="text-sm">
                  Sair da Conta
                </span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserContextSwitcher;